import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { Paths } from './app-routing.module';
import { DataService } from '@data/data.service';
import { StateService } from '@services/state.service';
import { ToolbarService } from './toolbar/toolbar.service';
import { isFunction, parseDec, setTeamNames, sortTeams } from '@util/utils';
import { BackendPaths } from '@common/backend-paths';
import { MatDialog } from '@angular/material/dialog';
import { SettingsDialogComponent } from '@toolbar/settings-dialog/settings-dialog.component';
import { MyLog } from '@common/common';
import { preventDefault, stopPropagation } from '@util/input-utils';
import { PrintReceiptsDialogComponent } from './montage-orders/print-receipts-dialog/print-receipts-dialog.component';
import { AppConfigutationKeys } from './app.configuration';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';

registerLocaleData(localeDe, 'de-DE', localeDeExtra);

interface MainPath {
  [K: string]: any[];
}

function p() {
  const result: any = {};

  for (let k in Paths) {
    let p = Paths[k];
    if (p.charAt(0) !== '/') {
      p = '/' + p;
    }
    result[k] = [p];

  }
  return result;
}

/*
function checkForMobile(mobileFn: any, delay = 2000) {
  if (!isFunction(mobileFn)) {
    MyLog.log('checkForMobile: No callback');
    return;
  }
  const elem = document.createElement('div');

  elem.id = 'test-elem';
  elem.style.width = '1in';
  elem.style.height = '1in';
  elem.style.position = 'fixed';
  elem.style.backgroundColor = 'transparent';
  elem.style.top = '200vh';
  elem.style.left = '200vw';

  document.body.appendChild(elem);

  setTimeout(() => {
    const elem = document.getElementById('test-elem');
    if (elem) {
      var dpi_x = elem.offsetWidth,
        dpi_y = elem.offsetHeight;
      MyLog.log('ME:offsets', dpi_x, dpi_y);

      if (dpi_x > 0 && dpi_y > 0 && screen.width > 0 && screen.height > 0) {
        var width = screen.width / dpi_x,
          height = screen.height / dpi_y;

        MyLog.log('Screen:dims', screen.width, screen.height);

        if (width < 6 || height < 6) {
          mobileFn(true);
        }
      }
      document.body.removeChild(elem);
    }
  }, delay);
}
*/

function createLinkPath(path: string, title: string) {
  if (path.charAt(0) !== '/') {
    path = '/' + path;
  }
  return {
    path: [path],
    title: title
  };
}

function hasGhostLogin() {
  if (window.localStorage) {
    let res = window.localStorage.getItem('ghost');
    return res === 'ok';
  }
  return false;
}

if (!(window as any).enableGhost) {
  (window as any).enableGhost = function (enable: boolean) {
    if (window.localStorage) {
      window.localStorage.setItem('ghost', enable ? 'ok' : '');
      window.location.reload();
      return;
    }
    console.log('LocalStorage not available');
  };
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent {

  title = 'Montage';

  paths: MainPath = p();

  loggedIn: boolean | null = null;

  isAdmin = false;

  hasGhost = false;

  ghost = '';

  password = '';

  username = '';

  planers: any[] = [];

  supportsMobile = false;

  adminPaths: any[] = [];

  montagePaths: any[] = [];

  activePaths: any = {};

  currentPath = '';

  isMobile = false;

  sidenavIsOpen = true;

  errorMessage = '';

  user_id = '';

  dialogLinkCls = 'active-btn-wrapper';

  constructor(
    private dataService: DataService,
    private router: Router,
    private stateService: StateService,
    private toolbarService: ToolbarService,
    private matDialog: MatDialog
  ) {

    for (let k in Paths) {
      this.activePaths[k] = ['active-btn-wrapper', 'active-btn'];
    }

    this.initNavPaths();
  }

  initNavPaths() {
    this.adminPaths = [
      createLinkPath(Paths.ORDERS_OPEN, 'offen'),
      createLinkPath(Paths.ORDERS_ASSIGNED, 'zugewiesen'),
      createLinkPath(Paths.ORDERS_COMPLETE, 'ausgeführt'),
      createLinkPath(Paths.ORDERS_CANCELLED, 'storniert'),
      createLinkPath(Paths.ORDERS_BILLED, 'abgerechnet')
    ];
    this.montagePaths = [
      createLinkPath(Paths.ORDERS_OPEN, 'offen'),
      createLinkPath(Paths.ORDERS_COMPLETE, 'ausgeführt'),
    ];
  }

  ngOnInit(): void {
    //Mit eigener ReuseStrategy hat es nicht funktioniert!
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.hasGhost = hasGhostLogin();

    this.toolbarService.changes().subscribe((newTitle: string) => {
      setTimeout(() => {
        this.title = newTitle;
      }, 0);
    });

    this.stateService.teamsChange().subscribe((teams: any[]) => {
      if (Array.isArray(teams)) {
        this.planers = teams.filter((item: any) => {
          return item.active;
        });
      }
    });

    this.stateService.mobileChange().subscribe((isMobile: boolean) => {
      //MyLog.log('Mobile:', isMobile);
      if (this.supportsMobile) {
        this.isMobile = isMobile;
      }
    });

    this.getStatus();
  }

  toggleSidenav(value?: boolean) {
    if (typeof value !== 'boolean') {
      value = !this.sidenavIsOpen
    }
    this.sidenavIsOpen = value;
  }

  gotoPage(url: any[]) {
    this.router.navigate(url).then((res: boolean) => {
      this.toggleSidenav(false);
    });
  }

  updateStatusFromData(data: any) {
    this.isAdmin = !!data?.admin;
    this.loggedIn = !!data?.status;
    this.stateService.isLoggedId = this.loggedIn;
    this.updateVersionView();
  }

  getStatus() {
    this.dataService.get(BackendPaths.STATUS).subscribe({
      next: (response: any) => {
        const data = response.result;
        this.updateStatusFromData(data);

        this.setTeams(data?.teams);
        this.user_id = data?.user_id || '';
        this.stateService.setConfiguration(data?.config);
        this.stateService.setIsAdmin(this.isAdmin);
        this.stateService.setStateKnown(true);
      },
      error: (error: any) => {
        this.loggedIn = false;
        MyLog.error('STATUS:ERROR:', error);
        this.router.navigate(['/']);
      }
    });
  }

  login() {
    const data: any = {
      username: this.username,
      password: this.password
    };


    if (this.hasGhost && this.ghost) {
      data.ghost = this.ghost;
      data.montage = '1';
      console.log('Ghost:login!');
    }

    this.errorMessage = '';

    this.stateService.isAppLoading = true;

    const url = data.ghost ? BackendPaths.LOGINGHOST : BackendPaths.LOGIN;

    this.dataService.post(url, data).subscribe((response: any) => {
      const data = response.result;
      if (data.error) {
        this.errorMessage = data.error;
        return;
      }

      this.updateStatusFromData(data);

      this.setTeams(data?.teams);
      this.user_id = data?.user_id || '';

      this.stateService.setConfiguration(data?.config);
      this.stateService.setIsAdmin(this.isAdmin);
      this.stateService.setStateKnown(true);

      this.router.navigate(this.isAdmin ? ['/orders'] : ['/orders/open']);
      /*
      setTimeout(() => {
        window.location.reload();
      }, 50);
      */

      this.stateService.isAppLoading = false;
    });
  }

  updateVersionView() {
    let fn = this.loggedIn ? (window as any).hideVersion : (window as any).showVersion;

    if (isFunction(fn)) {
      fn();
    }
  }

  setTeams(teams: any[]) {
    if (Array.isArray(teams)) {
      setTeamNames(teams);
      sortTeams(teams);
      this.stateService.setTeams(teams);
    }
  }

  logout() {
    this.dataService.get(BackendPaths.LOGOUT).subscribe((response: any) => {
      //TODO
      //const data = response.result;
      this.loggedIn = false;
      this.isAdmin = false;
      this.stateService.isLoggedId = false;
      //, {skipLocationChange: true}
      this.router.navigate(['/']).then((res: any) => {
        MyLog.log('Gone to /', res);
      });
      this.stateService.setIsAdmin(false);
      this.updateVersionView();
    });
  }

  showSettings() {
    const config: any = {};

    if (this.isAdmin) {
      config.width = '60vw';
      config.height = '60vh';
    }

    this.matDialog.open(
      SettingsDialogComponent,
      config
    );
  }

  showReceiptDialog($event: any) {
    if ($event) {
      preventDefault($event);
      stopPropagation($event);
    }

    const receipts = this.stateService.getLocalConfiguration(
      AppConfigutationKeys.RECEIPTS_VIEW
    ) || "2";

    if (parseDec(receipts || '') === 1) {
      this.matDialog.open(
        PrintReceiptsDialogComponent,
        {
          data: {},
          width: '80vw',
          height: '80vh'
        }
      );
    } else {
      this.router.navigate(['/receipt-print']).then((res: any) => {
        MyLog.log('Gone to /', res);
      });
    }
  }
}
